import React, { useState, useEffect, useMemo } from "react";
import { Card, Container, Row, Col } from "react-bootstrap";
import { FaChartBar, FaCheckCircle, FaClock, FaSyncAlt } from "react-icons/fa";
import { ThumbsUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./MyActivityDashboard.css";

const STATUS_COLORS = {
  Pending: "status-pending",
  Ongoing: "status-ongoing",
  Resolved: "status-resolved",
  Reopened: "status-reopened",
};

const STATUS_DOTS = {
  Pending: "#d97706",
  Ongoing: "#2563eb",
  Resolved: "#059669",
  Reopened: "#e11d48",
};

const STATUS_LABEL = {
  Pending: "Pending",
  Ongoing: "Ongoing",
  Resolved: "Resolved",
  Reopened: "Reopened",
};

const CATEGORY_ICONS = {
  Canteen: "🍽️",
  "IT and Networking": "🌐",
  EduPrime: "🎓",
  Infrastructure: "🏗️",
  Examination: "📝",
  Academics: "📚",
  "Fee Payments and Accounts": "💰",
  
  "Hostel Food": "🍲",
  "Extracurricular and Events": "🎭",
  Security: "🛡️",
  Sports: "⚽",
  Housekeeping: "🧹",
  "Audio-Visual Equipment": "📽️",
  Parking: "🅿️",
  Transport: "🚌",
  Library: "📚",
  Others: "📦",
};

const getCategoryIcon = (category) =>
  (category && CATEGORY_ICONS[category]) || "📝";

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

const getSupportNumber = (c) => {
  const likes = num(c.likes);
  const supportCount = num(c.supportCount);
  return likes > 0 ? likes : supportCount;
};

const StatusPill = ({ status }) => {
  const cls = (status && STATUS_COLORS[status]) || "status-other";
  const dot = (status && STATUS_DOTS[status]) || "#94a3b8";
  const label = (status && STATUS_LABEL[status]) || "Unknown";
  return (
    <span className={`status-pill ${cls}`}>
      <span className="status-dot" style={{ background: dot }} />
      {label}
    </span>
  );
};

const SummaryCard = ({ icon, number, label, accent, onClick }) => (
  <Card className={`summary-card summary-card-${accent}`} onClick={onClick} role="button" tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && onClick()}
  >
    <Card.Body className="text-center">
      {icon}
      <h3 className="summary-number">{number}</h3>
      <p className="summary-label">{label}</p>
    </Card.Body>
  </Card>
);

const IssueCard = ({ complaint, onOpen }) => {
  const support = getSupportNumber(complaint);
  const icon = getCategoryIcon(complaint.category);
  return (
    <div
      className="attention-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
    >
      <div className="attention-card-body">
        <div className="attention-card-title">
          <span className="attention-cat-emoji" aria-label={complaint.category || "Other"}>
            {icon}
          </span>
          <span className="attention-title-text">{complaint.title || "Untitled complaint"}</span>
        </div>
        <div className="attention-card-meta">
          <span className="attention-category-tag">
            {complaint.category || "Uncategorized"}
          </span>
          <StatusPill status={complaint.status} />
        </div>
        <div className="attention-support">
          <ThumbsUp size={16} />
          <span className="attention-support-text">
            {support} student{support === 1 ? "" : "s"} support this issue
          </span>
        </div>
      </div>
      <div className="attention-card-footer">
        <span className="attention-view-link">View Complaint →</span>
      </div>
    </div>
  );
};

const MyActivityDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userComplaints, setUserComplaints] = useState([]);
  const [allComplaints, setAllComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  useEffect(() => {
    if (!user?.email) return;

    const token = localStorage.getItem("authToken");
    let cancelled = false;

    const fetchUserComplaints = () =>
      axios
        .get(`${baseUrl}/user-api/view-complaints/${user.email}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (cancelled) return;
          const fetched = res.data.complaints || [];
          fetched.sort(
            (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
          );
          setUserComplaints(fetched);
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            localStorage.removeItem("authToken");
            navigate("/complaints-website");
          } else {
            setError(true);
          }
        });

    const fetchAllComplaints = () =>
      axios
        .get(
          `${baseUrl}/user-api/filter-complaints?category=&status=&_t=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then((res) => {
          if (cancelled) return;
          const fetched = res.data?.complaints || [];
          fetched.sort(
            (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
          );
          setAllComplaints(fetched);
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            localStorage.removeItem("authToken");
            navigate("/complaints-website");
          } else {
            setError(true);
          }
        });

    Promise.all([fetchUserComplaints(), fetchAllComplaints()]).finally(
      () => {
        if (cancelled) return;
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [user, baseUrl, navigate]);

  const stats = useMemo(() => {
    const total = userComplaints.length;
    const resolved = userComplaints.filter(
      (c) => c.status === "Resolved"
    ).length;
    const pending = userComplaints.filter(
      (c) => c.status === "Pending"
    ).length;
    const ongoing = userComplaints.filter(
      (c) => c.status === "Ongoing"
    ).length;
    return { total, pending, resolved, ongoing };
  }, [userComplaints]);

  const attentionComplaints = useMemo(() => {
    return allComplaints
      .filter(
        (c) =>
          (getSupportNumber(c) > 0 ||
          (c.comments && c.comments.length > 0)) && c.status !== "Resolved"
      )
      .sort((a, b) => {
        const sa = num(a.likes);
        const sb = num(b.likes);
        if (sb !== sa) return sb - sa;
        if (num(b.supportCount) !== num(a.supportCount))
          return num(b.supportCount) - num(a.supportCount);
        return new Date(b.timestamp) - new Date(a.timestamp);
      })
      .slice(0, 6);
  }, [allComplaints]);

  if (loading) {
    return (
      <div className="my-activity-container">
        <div className="dashboard-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const displayName = user?.name || "Student";

  return (
    <div className="my-activity-container">
      <ToastContainer />
      <Container className="dashboard-wrapper">
        {/* 1. Welcome Section */}
        <div className="dashboard-welcome">
          <h1 className="welcome-title">Hi, {displayName} 👋</h1>
          <p className="welcome-subtitle">
            Here's a quick overview of your activity.
          </p>
        </div>

        {/* 2. Personal Summary */}
        <Row className="summary-cards-row g-3 mb-4">
          <Col xs={6} md={3}>
            <SummaryCard
              accent="myrequests"
              icon={<FaChartBar className="summary-icon" />}
              number={stats.total}
              label="My Requests"
              onClick={() => navigate("/my-complaints")}
            />
          </Col>
          <Col xs={6} md={3}>
            <SummaryCard
              accent="pending"
              icon={<FaClock className="summary-icon" />}
              number={stats.pending}
              label="Pending"
              onClick={() => navigate("/my-complaints")}
            />
          </Col>
          <Col xs={6} md={3}>
            <SummaryCard
              accent="ongoing"
              icon={<FaSyncAlt className="summary-icon" />}
              number={stats.ongoing}
              label="Ongoing"
              onClick={() => navigate("/my-complaints")}
            />
          </Col>
          <Col xs={6} md={3}>
            <SummaryCard
              accent="resolved"
              icon={<FaCheckCircle className="summary-icon" />}
              number={stats.resolved}
              label="Resolved"
              onClick={() => navigate("/my-complaints")}
            />
          </Col>
        </Row>

        {/* 3. Issues Getting Attention */}
        <div className="attention-section">
          <div className="attention-section-head">
            <h2 className="attention-title">🔥 Issues Getting Attention</h2>
            <p className="attention-subtitle">
              Complaints receiving the most community support
            </p>
          </div>

          {error ? (
            <div className="attention-empty text-center">
              <p className="text-muted mb-0">
                Unable to load community activity right now.
              </p>
            </div>
          ) : attentionComplaints.length === 0 ? (
            <div className="attention-empty">
              <span className="attention-empty-icon">📭</span>
              <p className="attention-empty-title">
                No issues receiving attention yet
              </p>
              <p className="attention-empty-sub">
                When complaints gain support from the community, they'll appear
                here.
              </p>
            </div>
          ) : (
            <div className="attention-cards">
              {attentionComplaints.map((complaint) => (
                <IssueCard
                  key={complaint.complaint_id}
                  complaint={complaint}
                  onOpen={() =>
                    navigate(`/complaints-details/${complaint.complaint_id}`)
                  }
                />
              ))}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default MyActivityDashboard;
